/**
 * Transparent Cognito access-token refresh for every authed request (feat-028).
 *
 * Background: feat-026 made the SPA send the Cognito *access* token, but nothing
 * ever refreshed it. Cognito access tokens live ~1h, so a long session sent a
 * stale token — a graceful no-role under feat-027 shadow, but a hard 401 (→
 * hourly logout) once feat-023/027 flip to enforce. This module closes that gap.
 *
 * Two layers, applied centrally at the authed-fetch wrappers (api.ts authedFetch,
 * managementService, RestaurantBackend.apiCall):
 *   - Proactive: before sending, if the access token is expired or within a small
 *     skew of expiry, refresh it first (getFreshAuthToken).
 *   - Reactive: on a 401, refresh ONCE and retry; if that refresh fails, clear the
 *     session and route to login — no retry loop (authedApiFetch).
 *
 * Refreshes are single-flight: concurrent callers share one in-flight refresh so
 * an expiry never triggers N parallel token exchanges. The actual Cognito call
 * lives in cognitoAuthService.refreshSession(). Never logs token bytes.
 */
import { config } from '../config/environment';
import CognitoAuthService from './cognitoAuthService';
import { getAuthToken } from './authToken';

const ACCESS_TOKEN_KEY = 'cognito_access_token';
const REFRESH_TOKEN_KEY = 'cognito_refresh_token';
const LOGIN_PATH = '/scorecard-login';
/** Refresh when the access token is within this many seconds of expiry. */
const EXPIRY_SKEW_SECONDS = 120;

const cognito = new CognitoAuthService(config.cognito);

/** Single-flight guard: concurrent callers await the same in-flight refresh. */
let inflightRefresh: Promise<string | null> | null = null;

/** Decode a JWT's `exp` (seconds since epoch) without verifying. Null if unparseable. */
const decodeJwtExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
};

/** True when the token has no exp, is already expired, or is within the skew of expiry. */
export const isAccessTokenExpiring = (
  token: string,
  skewSeconds: number = EXPIRY_SKEW_SECONDS,
): boolean => {
  const exp = decodeJwtExp(token);
  if (exp === null) return true;
  const nowSeconds = Date.now() / 1000;
  return exp - nowSeconds <= skewSeconds;
};

/**
 * Exchange the stored refresh token for a fresh access (+ id) token. Single-flight:
 * concurrent callers share one network refresh. Resolves to the new access token,
 * or null when there is no refresh token or Cognito rejects it.
 */
export const refreshAccessToken = (): Promise<string | null> => {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const result = await cognito.refreshSession();
      return result ? result.accessToken : null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
};

/**
 * Proactive freshness: returns a valid bearer token for an authed request,
 * refreshing first when the Cognito access token is expired/near expiry. Falls
 * back to whatever getAuthToken() yields (legacy token or null) when there is no
 * Cognito session. A failed proactive refresh is non-fatal — the (possibly still
 * valid) token is returned and the reactive 401 path takes over if the server
 * actually rejects it.
 */
export const getFreshAuthToken = async (): Promise<string | null> => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) return getAuthToken();
  if (!isAccessTokenExpiring(accessToken)) return accessToken;
  const refreshed = await refreshAccessToken();
  return refreshed ?? accessToken;
};

/** Clear the session and route to login. Called when a refresh fails. */
export const redirectToLogin = (): void => {
  cognito.clearStoredTokens();
  if (typeof window !== 'undefined') {
    window.location.assign(LOGIN_PATH);
  }
};

/**
 * Return a new options object whose Authorization header carries `token` (when
 * we have one), preserving the caller's other headers AND their plain-object
 * shape. When `token` is null we leave the caller's headers untouched — never
 * strip an Authorization the caller set explicitly (e.g. consentEvidenceService
 * passes its own header). A fresh token overrides a stale one of the same value.
 */
const withBearer = (options: RequestInit, token: string | null): RequestInit => {
  const merged: Record<string, string> = {};
  const existing = options.headers;
  if (existing instanceof Headers) {
    existing.forEach((value, key) => {
      merged[key] = value;
    });
  } else if (Array.isArray(existing)) {
    for (const [key, value] of existing) merged[key] = value;
  } else if (existing) {
    Object.assign(merged, existing as Record<string, string>);
  }
  if (token) {
    // Drop any existing auth header (Headers.forEach lowercases keys, so a
    // caller-set 'Authorization' arrives as 'authorization') before setting
    // ours — otherwise the two casings collide into a duplicated header value.
    for (const key of Object.keys(merged)) {
      if (key.toLowerCase() === 'authorization') delete merged[key];
    }
    merged['Authorization'] = `Bearer ${token}`;
  }
  return { ...options, headers: merged };
};

/**
 * Authed fetch against an absolute URL with transparent token refresh:
 *  - proactively refreshes a near-expiry access token before sending,
 *  - on a 401, refreshes ONCE and retries the request,
 *  - if that refresh fails, clears the session and routes to login (no retry loop).
 *
 * Shared by managementService and RestaurantBackend.apiCall so every Cognito-authed
 * path stays fresh from one place.
 */
export const authedApiFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const token = await getFreshAuthToken();
  const response = await fetch(url, withBearer(options, token));
  if (response.status !== 401) return response;

  // Reactive belt-and-suspenders: exactly one refresh + retry on a 401. With no
  // refresh token there is nothing to retry — return the 401 as-is.
  if (!localStorage.getItem(REFRESH_TOKEN_KEY)) return response;
  const newToken = await refreshAccessToken();
  if (!newToken) {
    redirectToLogin();
    return response;
  }
  return fetch(url, withBearer(options, newToken));
};
